// useBillingStatus — 메뉴 배지·홈 띠용 가벼운 멤버십 상태 (2026-09-22, 대표 결정 「멤버십 메뉴는 권고안대로」)
//
// 사이드바 「계정 › 멤버십」 배지 · 상단 프로필 메뉴 「멤버십」 배지 · 홈 최상단 「지금 할 일」 띠가 같은 화면에서 동시에 쓰므로
// RPC billing_overview 를 한 번만 부르고 모듈 캐시(5분)로 나눠 쓴다. /billing 화면의 useBillingOverview 는 캐시하지 않고
// 매번 새로 읽는데, 그 결과를 primeBillingStatus 로 여기에 밀어 넣어 결제·해지 직후 배지와 띠가 바로 따라오게 한다.
// 실패하면 null — 배지·띠는 그리지 않는다(화면은 그대로).
import { useEffect, useState } from "react";
import { fetchOverview } from "../lib/billingApi";

const TTL_MS = 5 * 60 * 1000;
let cache = null; // { email, at, overview }
let inflight = null; // { email, promise }
const listeners = new Set();

function emit() { listeners.forEach((fn) => { try { fn(cache ? cache.overview : null); } catch { /* noop */ } }); }

/** /billing 화면이 새로 읽은 overview 를 공유 캐시에 반영한다(결제·해지 직후 배지·띠 즉시 갱신). */
export function primeBillingStatus(email, overview) {
  if (!email || !overview) return;
  cache = { email: String(email).toLowerCase(), at: Date.now(), overview };
  emit();
}

export function _resetBillingStatusCache() { cache = null; inflight = null; }

async function load(email) {
  const key = String(email).toLowerCase();
  if (cache && cache.email === key && Date.now() - cache.at < TTL_MS) return cache.overview;
  if (inflight && inflight.email === key) return inflight.promise;
  // 🔒 반드시 email 을 넘긴다. 안 넘기면 서버가 auth.jwt() 의 로그인 계정을 보는데,
  // 어드민이 다른 회원으로 보고 있을 때(impersonate) **어드민 본인의 결제 상태**가 그 회원 화면에 뜬다
  // (2026-09-23 실측 — 기존 회원 화면에 어드민의 「재활성화비 150,000원」 띠가 떴다). billing_overview 는 어드민의 대리 조회를 허용한다.
  const promise = fetchOverview(email)
    .then((data) => { cache = { email: key, at: Date.now(), overview: data || { active: false } }; inflight = null; emit(); return cache.overview; })
    .catch((err) => { inflight = null; throw err; });
  inflight = { email: key, promise };
  return promise;
}

export default function useBillingStatus(email, { enabled = true } = {}) {
  const [overview, setOverview] = useState(() => (cache && email && cache.email === String(email).toLowerCase() ? cache.overview : null));

  useEffect(() => {
    if (!enabled || !email) { setOverview(null); return undefined; }
    let alive = true;
    const onChange = (ov) => { if (alive) setOverview(ov); };
    listeners.add(onChange);
    load(email).then((ov) => { if (alive) setOverview(ov); }).catch(() => { if (alive) setOverview(null); });
    return () => { alive = false; listeners.delete(onChange); };
  }, [email, enabled]);

  return overview;
}
